import React from 'react'
import { addNotification, useDispatch } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, sortBy } from 'lodash'
import { CategoryTypeNode, CategoryNode, SubCategoryNode, Jsonapi } from '../../definitions'
import { getDrupalSetting } from '@ggs/utils'

/**
 * @typedef {(categoryTypeNid: string) => Promise<CategoryNode[]>} GetCategories
 * @typedef {(categoryTypeNid: string) => Promise<SubCategoryNode[]>} GetSubcategories
 * @typedef {() => Promise<CategoryTypeNode[]>} GetCategoryTypes
 */

/**
 * @typedef {Object} DrupalApiHook
 * @property {GetCategories} getCategories
 * @property {GetSubcategories} getSubcategories
 * @property {GetCategoryTypes} getCategoryTypes
 */

/**
 * Hook that expose utilities to retrieve Drupal JSON API data.
 * @return {DrupalApiHook}
 */
function useDrupalApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl =
    getDrupalSetting('baseUrl') || `${process.env.REACT_APP_DRUPAL_BASE_URL}` || 'http://localhost'
  const basePath = 'api'
  const authToken = `${process.env.REACT_APP_DRUPAL_API_TOKEN}`

  /**
   * Check current user active Drupal session.
   */
  const checkActiveSession = async () => {
    const response = await doRequest(basePath)
    const userUUID = get(response, 'meta.links.me.href', null)

    return Boolean(userUUID)
  }

  /**
   * Process a Drupal API endpoint request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @return {Promise<Jsonapi>}
   */
  const doRequest = async (endpoint, data = null, customHeaders) => {
    const url = `${baseUrl}/${endpoint}`
    const isStorybook = `${process.env.STORYBOOK}` === 'yes'
    console.log('doRequestURL:', url)
    const authHeader = {
      Authorization: `Bearer ${authToken}`,
    }

    /**
     * @type {RequestInit}
     */
    const options = {
      method: 'POST',
      headers: {
        //'Content-Type': 'application/json',
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        ...(authToken && isStorybook ? authHeader : {}),
        ...(customHeaders || {}),
      },
    }

    if (data) {
      options.body = JSON.stringify(data)
    } else {
      options.method = 'GET'
    }

    try {
      const response = await fetch(url, options)

      // If is a success response return data, otherwise null to indicate failure.
      if ([200].includes(response.status)) {
        return await response.json()
      } else if ([422].includes(response.status)) {
        const data = await response.json()
        return {
          ...data,
          errorCode: 422,
        }
      } else if ([403].includes(response.status)) {
        const hasValidSession = await checkActiveSession()
        if (hasValidSession) {
          // Session is valid but user don't have privilege granted for the consumed endpoint.
          dispatch(
            addNotification(
              'drupal_api_request_failed',
              'error',
              t('Drupal API request was forbidden for current user.')
            )
          )
        } else {
          // Session is expired, user need to authenticate again, redirect to Drupal login.
          window.location.href = `${baseUrl}/user`
        }
      }

      return null
    } catch (error) {
      console.log('ERROR Drupal API:', error)
      return null
    }
  }

  /**
   * Get all child subcategories for specific category type.
   * @param {string} categoryTypeNid
   * @return {Promise<SubCategoryNode[]>}
   */
  const getSubcategories = async (categoryTypeNid) => {
    const endpoint = `${basePath}/node/subcategory`
    const params = `include=category&filter[category.meta.drupal_internal__target_id]=${categoryTypeNid}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const data = response?.data || null

    if (!data) {
      return null
    }

    const entities = data.map((item) => {
      return /** @type {SubCategoryNode} */ (item.attributes)
    })

    return sortBy(entities, ['title'])
  }

  /**
   * Get all child categories for specific category type.
   * @param {string} categoryTypeNid
   * @return {Promise<CategoryNode[]>}
   */
  const getCategories = async (categoryTypeNid) => {
    const endpoint = `${basePath}/node/category`
    const params = `include=category_type&filter[category_type.meta.drupal_internal__target_id]=${categoryTypeNid}`
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const data = response?.data || null

    if (!data) {
      return null
    }

    const entities = data.map((item) => {
      return /** @type {CategoryNode} */ (item.attributes)
    })

    return sortBy(entities, ['title'])
  }

  /**
   * Get all category types.
   * @return {Promise<CategoryTypeNode[]>}
   */
  const getCategoryTypes = async () => {
    const endpoint = `${basePath}/node/category_type`
    const params = ''
    const response = await doRequest(endpoint + '?' + encodeURI(params))
    const data = response?.data || null

    if (!data) {
      return null
    }

    return data.map((item) => {
      return /** @type {CategoryTypeNode} */ (item.attributes)
    })
  }

  return {
    getSubcategories,
    getCategories,
    getCategoryTypes,
  }
}

export default useDrupalApi
